import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { chordsForProgression, playChord } from "../lib/keyboardPlan";
import {
  randomTranslateQ,
  randomBuildTarget,
  randomEarQ,
  randomPredictQ,
  randomSongQ,
  randomTonicQ,
  randomCadenceQ,
  earReadyKeys,
  earSlotGrade,
  earSlotResults,
  degStr,
  pick,
  questionConcept,
  conceptKey,
  gameXp,
  GAME_ROUNDS,
} from "../lib/numberSenseData";
import {
  Icon,
  Piano,
  PlayButton,
  EarSlots,
  MidiBadge,
  AudioSettingsContext,
} from "./numberSenseUi";
import useMidi from "./numberSense/useMidi";

/* ============================================================
   Number Sense — practice games (an aside to the lessons)
   Each game is self-contained: it takes the learner's unlocked
   material as props, runs its own loop, and reports XP back via
   onAward. None touch hearts or lesson progress — just for play.

   Shared props:
     data      : { pairs:[{key,deg}], unlocked:[..], earKeys:[..], songs:[..] }
     onExit    : () => void   back to the games hub
     onAward   : (xp) => void
     onResults : (results) => void  optional; batch of { concept, correct } for
                 per-concept mastery, reported once on game-over (only the
                 concept-level games — translate / builder — emit these).
   ============================================================ */

/* ---------------- metadata for the hub ---------------- */
export const GAMES = [
  {
    key: "translate",
    kind: "translate",
    icon: "swap",
    title: "number ⇄ chord",
    sub: "Translator",
    desc: "Beat the clock: name the chord for a number, or the number for a chord.",
  },
  {
    key: "builder",
    kind: "builder",
    icon: "piano",
    title: "build the chord",
    sub: "Chord builder",
    desc: "Tap out each chord on the keyboard — any octave, any order.",
  },
  {
    key: "ear",
    kind: "ear",
    icon: "ear",
    title: "name the changes",
    sub: "Ear training",
    desc: "Hear a four-chord loop and call the numbers you heard.",
  },
  {
    key: "song",
    kind: "song",
    icon: "music",
    title: "name that song",
    sub: "Progression ID",
    desc: "Hear a real song's changes and name its numbers — or the song.",
  },
  {
    key: "predict",
    kind: "predict",
    icon: "ear",
    title: "call the next chord",
    sub: "Prediction",
    desc: "Hear most of a loop, then call the last chord before it lands.",
  },
  {
    key: "tonic",
    kind: "tonic",
    icon: "ear",
    title: "find home",
    sub: "Hear the tonic",
    desc: "Hear a cadence resolve, then pick the note that feels like home.",
  },
  {
    key: "cadence",
    kind: "cadence",
    icon: "ear",
    title: "name the cadence",
    sub: "Harmonic function",
    desc: "Hear a two-chord move and call it: authentic, plagal, half, deceptive.",
  },
];

const LOCK_MSG = {
  translate: "unlock 3 chords first",
  builder: "unlock 3 chords first",
  ear: "reach a key with 3+ chords",
  song: "reach a starter song's key",
  predict: "reach a key with 3+ chords",
  tonic: "reach a key with 3+ chords",
  cadence: "reach a key with 3+ chords",
};
export const lockMessage = (kind) => LOCK_MSG[kind] || "locked";

/* ============================================================
   1 · Translator — 30s of rapid number ⇄ chord recall
   ============================================================ */
const SPEED_SECONDS = 30;

export function TranslatorRound({ data, onExit, onAward, onResults }) {
  const [phase, setPhase] = useState("play");
  const [timeLeft, setTimeLeft] = useState(SPEED_SECONDS);
  const [score, setScore] = useState(0);
  const [q, setQ] = useState(() => randomTranslateQ(data.pairs));
  const [picked, setPicked] = useState(null);
  const awarded = useRef(false);
  const results = useRef([]);
  const qStart = useRef(Date.now()); // response time feeds fluency mastery

  useEffect(() => {
    if (phase !== "play") return undefined;
    const t = setInterval(() => setTimeLeft((tl) => (tl <= 1 ? 0 : tl - 1)), 1000);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (timeLeft === 0 && phase === "play") {
      setPhase("over");
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("translate", score));
        if (onResults) onResults(results.current);
      }
    }
  }, [timeLeft, phase, score, onAward, onResults]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    const isCorrect = opt === q.correct;
    if (isCorrect) setScore((s) => s + 1);
    const concept = questionConcept(q);
    if (concept)
      results.current.push({
        concept,
        correct: isCorrect,
        ms: Date.now() - qStart.current,
        type: q.type,
      });
    setTimeout(() => {
      setPicked(null);
      setQ(randomTranslateQ(data.pairs));
      qStart.current = Date.now();
    }, 340);
  };

  const restart = () => {
    awarded.current = false;
    results.current = [];
    setScore(0);
    setTimeLeft(SPEED_SECONDS);
    setPicked(null);
    setQ(randomTranslateQ(data.pairs));
    qStart.current = Date.now();
    setPhase("play");
  };

  if (phase === "over") {
    return (
      <GameDone
        title="time!"
        sub="nicely played"
        cards={[
          { k: "score", v: score, accent: "var(--teal)" },
          { k: "XP earned", v: `+${score}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className={`timer ${timeLeft <= 5 ? "low" : ""}`}>
          <Icon name="timer" />
          {timeLeft}s
        </div>
        <div className="game-score">
          <Icon name="bolt" />
          {score}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">
          {q.dir === "num2chord" ? "name the chord" : "name the number"}
        </div>
        <div className="qprompt">{q.prompt}</div>
        <div className={`choices${q.options.length > 4 ? " many" : ""}`}>
          {q.options.map((opt) => {
            let cls = "choice";
            if (picked) {
              if (opt === q.correct) cls += " right";
              else if (opt === picked) cls += " wrong";
              else cls += " dim";
            }
            return (
              <button key={opt} className={cls} onClick={() => choose(opt)}>
                <span className="mono">{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ============================================================
   2 · Chord builder — build N chords on the on-screen piano
   ============================================================ */
const BUILD_ROUNDS = GAME_ROUNDS.builder;

export function ChordBuilderGame({ data, onExit, onAward, onResults }) {
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(() => randomBuildTarget(data.pairs));
  const [pressed, setPressed] = useState(() => new Set());
  const [result, setResult] = useState(null); // null | "right" | "wrong"
  const [score, setScore] = useState(0);
  const awarded = useRef(false);
  const results = useRef([]);
  const targetStart = useRef(Date.now()); // response time feeds fluency mastery

  const toggle = (name) =>
    setPressed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const press = (k) => {
    if (result) return;
    toggle(k.name);
  };

  // A real MIDI keyboard fills the same set without touching the screen.
  const midi = useMidi((k) => {
    if (!result) toggle(k.name);
  });

  const check = () => {
    const want = new Set(target.targetNotes);
    const ok =
      pressed.size === want.size && [...want].every((n) => pressed.has(n));
    setResult(ok ? "right" : "wrong");
    if (ok) setScore((s) => s + 1);
    results.current.push({
      concept: conceptKey(target),
      correct: ok,
      ms: Date.now() - targetStart.current,
      type: "build",
    });
  };

  const next = () => {
    const nr = round + 1;
    if (nr >= BUILD_ROUNDS) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("builder", score));
        if (onResults) onResults(results.current);
      }
      setRound(nr);
      return;
    }
    setRound(nr);
    setTarget(randomBuildTarget(data.pairs));
    setPressed(new Set());
    setResult(null);
    targetStart.current = Date.now();
  };

  const restart = () => {
    awarded.current = false;
    results.current = [];
    setRound(0);
    setTarget(randomBuildTarget(data.pairs));
    setPressed(new Set());
    setResult(null);
    setScore(0);
    targetStart.current = Date.now();
  };

  if (round >= BUILD_ROUNDS) {
    return (
      <GameDone
        title="set complete"
        sub="chords built"
        cards={[
          { k: "correct", v: `${score}/${BUILD_ROUNDS}`, accent: "var(--teal)" },
          { k: "XP earned", v: `+${gameXp("builder", score)}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">build it</div>
        <div className="game-score">
          {round + 1}/{BUILD_ROUNDS}
        </div>
      </div>
      <div className="qarea">
        <div className="qprompt">
          play <b>{target.label}</b> <span className="muted">({target.key} · {target.deg})</span>
        </div>
        <Piano
          pressed={pressed}
          onPress={press}
          targetNotes={target.targetNotes}
          result={result}
          disabled={!!result}
        />
        <div className="ns-built">
          {pressed.size ? [...pressed].join(" · ") : "tap the notes…"}
        </div>
        <MidiBadge status={midi.status} devices={midi.devices} />
      </div>
      <div className={`foot ${result || ""}`}>
        {!result ? (
          <button className="btn" disabled={pressed.size < 3} onClick={check}>
            Check
          </button>
        ) : (
          <>
            <div className="verdict">
              <b>{result === "right" ? "nailed it" : "not quite"}</b>
              <small>
                {result === "right"
                  ? "that's the chord"
                  : `${target.label} is ${target.targetNotes.join("–")}`}
              </small>
            </div>
            <button
              className={`btn ${result === "right" ? "" : "loje"}`}
              onClick={next}
            >
              {round + 1 >= BUILD_ROUNDS ? "Finish" : "Next"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ============================================================
   3 · Ear training — hear a loop, name it bar by bar
   ============================================================ */
const EAR_ROUNDS = GAME_ROUNDS.ear;

export function EarRound({ data, onExit, onAward, onResults }) {
  const keys = data.earKeys.length ? data.earKeys : earReadyKeys(data.unlocked);
  const [round, setRound] = useState(0);
  const [q, setQ] = useState(() => randomEarQ(pick(keys)));
  const [picks, setPicks] = useState([]);
  const [verdict, setVerdict] = useState(null); // earSlotGrade result
  const [score, setScore] = useState(0);
  const awarded = useRef(false);
  const results = useRef([]); // per-slot ear results, batched on game-over

  const chords = useMemo(() => chordsForProgression(q.key, q.degrees), [q]);

  const check = () => {
    if (verdict) return;
    const v = earSlotGrade(q.degrees, picks);
    setVerdict(v);
    if (v.passed) setScore((s) => s + 1);
    results.current.push(...earSlotResults(q.key, v.slots));
  };

  const next = () => {
    const nr = round + 1;
    if (nr >= EAR_ROUNDS) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("ear", score));
        if (onResults) onResults(results.current);
      }
      setRound(nr);
      return;
    }
    setRound(nr);
    setQ(randomEarQ(pick(keys)));
    setPicks([]);
    setVerdict(null);
  };

  const restart = () => {
    awarded.current = false;
    results.current = [];
    setRound(0);
    setQ(randomEarQ(pick(keys)));
    setPicks([]);
    setVerdict(null);
    setScore(0);
  };

  if (round >= EAR_ROUNDS) {
    return (
      <GameDone
        title="good ears"
        sub="loops named"
        cards={[
          { k: "correct", v: `${score}/${EAR_ROUNDS}`, accent: "var(--teal)" },
          { k: "XP earned", v: `+${gameXp("ear", score)}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">key of {q.key}</div>
        <div className="game-score">
          {round + 1}/{EAR_ROUNDS}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">listen, then name the numbers — bar by bar</div>
        <div className="ns-listen">
          <PlayButton chords={chords} big label="play the loop" />
        </div>
        <EarSlots
          pool={q.pool}
          count={q.degrees.length}
          picks={picks}
          onPicks={setPicks}
          answered={Boolean(verdict)}
          results={verdict && verdict.slots.map((s) => s.correct)}
        />
      </div>
      <div className={`foot ${verdict ? (verdict.passed ? "right" : "wrong") : ""}`}>
        {!verdict ? (
          <button
            className="btn"
            disabled={picks.length < q.degrees.length}
            onClick={check}
          >
            Check
          </button>
        ) : (
          <>
            <div className="verdict">
              <b>
                {!verdict.passed
                  ? "listen again"
                  : verdict.right === verdict.total
                  ? "perfect ears"
                  : "good ears"}
              </b>
              <small>
                {verdict.right}/{verdict.total} bars — it was {q.correct}
              </small>
            </div>
            <button
              className={`btn ${verdict.passed ? "" : "loje"}`}
              onClick={next}
            >
              {round + 1 >= EAR_ROUNDS ? "Finish" : "Next"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ============================================================
   3b · Call the next chord — hear most of a loop, predict the last
   (keyboardPlan Phase 5.2: hear where it's going before it lands)
   ============================================================ */
const PREDICT_ROUNDS = GAME_ROUNDS.predict;

export function PredictRound({ data, onExit, onAward, onResults }) {
  const keys = data.earKeys.length ? data.earKeys : earReadyKeys(data.unlocked);
  const settings = useContext(AudioSettingsContext);
  const [round, setRound] = useState(0);
  const [q, setQ] = useState(() => randomPredictQ(pick(keys)));
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const awarded = useRef(false);
  const results = useRef([]);

  const heardChords = useMemo(() => chordsForProgression(q.key, q.heard), [q]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    const ok = opt === q.correct;
    if (ok) setScore((s) => s + 1);
    const concept = questionConcept(q);
    if (concept) results.current.push({ concept, correct: ok, type: q.type });
    // resolve audibly: replay the last chord heard, then land the real answer
    const tempo = settings.tempo;
    chordsForProgression(q.key, [q.heard[q.heard.length - 1], q.correct]).forEach(
      (notes, i) => playChord(notes, tempo * 0.95, (i * tempo) / 1000)
    );
  };

  const next = () => {
    const nr = round + 1;
    if (nr >= PREDICT_ROUNDS) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("predict", score));
        if (onResults) onResults(results.current);
      }
      setRound(nr);
      return;
    }
    setRound(nr);
    setQ(randomPredictQ(pick(keys)));
    setPicked(null);
  };

  const restart = () => {
    awarded.current = false;
    results.current = [];
    setRound(0);
    setQ(randomPredictQ(pick(keys)));
    setPicked(null);
    setScore(0);
  };

  if (round >= PREDICT_ROUNDS) {
    return (
      <GameDone
        title="called it"
        sub="changes predicted"
        cards={[
          { k: "correct", v: `${score}/${PREDICT_ROUNDS}`, accent: "var(--teal)" },
          { k: "XP earned", v: `+${gameXp("predict", score)}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">key of {q.key}</div>
        <div className="game-score">
          {round + 1}/{PREDICT_ROUNDS}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">
          hear {q.heard.length} chords — what comes next?
        </div>
        <div className="ns-listen">
          <PlayButton chords={heardChords} big label="play the setup" />
        </div>
        <div className={`choices${q.options.length > 4 ? " many" : ""}`}>
          {q.options.map((opt) => {
            let cls = "choice";
            if (picked) {
              if (opt === q.correct) cls += " right";
              else if (opt === picked) cls += " wrong";
              else cls += " dim";
            }
            return (
              <button key={opt} className={cls} onClick={() => choose(opt)}>
                <span className="mono">{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={`foot ${picked ? (picked === q.correct ? "right" : "wrong") : ""}`}>
        {picked && (
          <>
            <div className="verdict">
              <b>{picked === q.correct ? "called it" : "hear it resolve"}</b>
              <small>
                {degStr(q.heard)} → <span>{q.correct}</span>
              </small>
            </div>
            <button
              className={`btn ${picked === q.correct ? "" : "loje"}`}
              onClick={next}
            >
              {round + 1 >= PREDICT_ROUNDS ? "Finish" : "Next"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ============================================================
   4 · Name that song — identify a starter song's progression
   ============================================================ */
const SONG_ROUNDS = GAME_ROUNDS.song;

export function SongRound({ data, onExit, onAward }) {
  const [round, setRound] = useState(0);
  const [q, setQ] = useState(() => randomSongQ(data.songs));
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const awarded = useRef(false);

  const chords = useMemo(() => chordsForProgression(q.key, q.degrees), [q]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt === q.correct) setScore((s) => s + 1);
  };

  const next = () => {
    const nr = round + 1;
    if (nr >= SONG_ROUNDS) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("song", score));
      }
      setRound(nr);
      return;
    }
    setRound(nr);
    setQ(randomSongQ(data.songs));
    setPicked(null);
  };

  const restart = () => {
    awarded.current = false;
    setRound(0);
    setQ(randomSongQ(data.songs));
    setPicked(null);
    setScore(0);
  };

  if (round >= SONG_ROUNDS) {
    return (
      <GameDone
        title="that's a wrap"
        sub="songs called"
        cards={[
          { k: "correct", v: `${score}/${SONG_ROUNDS}`, accent: "var(--teal)" },
          { k: "XP earned", v: `+${gameXp("song", score)}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">key of {q.key}</div>
        <div className="game-score">
          {round + 1}/{SONG_ROUNDS}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">
          {q.ask === "title" ? "name the song" : "name the numbers"}
        </div>
        <div className="ns-listen">
          <PlayButton chords={chords} big label="play the changes" />
        </div>
        <div className="choices">
          {q.options.map((opt) => {
            let cls = "choice";
            if (picked) {
              if (opt === q.correct) cls += " right";
              else if (opt === picked) cls += " wrong";
              else cls += " dim";
            }
            return (
              <button key={opt} className={cls} onClick={() => choose(opt)}>
                <span className={q.ask === "title" ? "" : "mono"}>{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={`foot ${picked ? (picked === q.correct ? "right" : "wrong") : ""}`}>
        {picked && (
          <>
            <div className="verdict">
              <b>{picked === q.correct ? "got it" : "close"}</b>
              <small>
                {q.ask === "title"
                  ? `${q.songTitle} — ${q.artist}`
                  : `${q.songTitle}: ${q.correct}`}
              </small>
            </div>
            <button
              className={`btn ${picked === q.correct ? "" : "loje"}`}
              onClick={next}
            >
              {round + 1 >= SONG_ROUNDS ? "Finish" : "Next"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ============================================================
   5 · Find home — hear a cadence resolve, pick the tonic note
   ============================================================ */
const TONIC_ROUNDS = GAME_ROUNDS.tonic;

export function TonicRound({ data, onExit, onAward }) {
  const keys = data.earKeys.length ? data.earKeys : earReadyKeys(data.unlocked);
  const [round, setRound] = useState(0);
  const [q, setQ] = useState(() => randomTonicQ(pick(keys)));
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const awarded = useRef(false);

  const chords = useMemo(() => chordsForProgression(q.key, q.degrees), [q]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt === q.correct) setScore((s) => s + 1);
  };

  const next = () => {
    const nr = round + 1;
    if (nr >= TONIC_ROUNDS) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("tonic", score));
      }
      setRound(nr);
      return;
    }
    setRound(nr);
    setQ(randomTonicQ(pick(keys)));
    setPicked(null);
  };

  const restart = () => {
    awarded.current = false;
    setRound(0);
    setQ(randomTonicQ(pick(keys)));
    setPicked(null);
    setScore(0);
  };

  if (round >= TONIC_ROUNDS) {
    return (
      <GameDone
        title="dialed in"
        sub="homes found"
        cards={[
          { k: "correct", v: `${score}/${TONIC_ROUNDS}`, accent: "var(--teal)" },
          { k: "XP earned", v: `+${gameXp("tonic", score)}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        {/* the key is hidden on purpose — the whole game is hearing "home" */}
        <div className="qkind">where's home?</div>
        <div className="game-score">
          {round + 1}/{TONIC_ROUNDS}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">hear it resolve, then pick the tonic</div>
        <div className="ns-listen">
          <PlayButton chords={chords} big label="play the cadence" />
        </div>
        <div className="choices">
          {q.options.map((opt) => {
            let cls = "choice";
            if (picked) {
              if (opt === q.correct) cls += " right";
              else if (opt === picked) cls += " wrong";
              else cls += " dim";
            }
            return (
              <button key={opt} className={cls} onClick={() => choose(opt)}>
                <span className="mono">{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={`foot ${picked ? (picked === q.correct ? "right" : "wrong") : ""}`}>
        {picked && (
          <>
            <div className="verdict">
              <b>{picked === q.correct ? "that's home" : "not quite"}</b>
              <small>
                {picked === q.correct
                  ? `${q.key} resolves to ${q.correct}`
                  : `home was ${q.correct}`}
              </small>
            </div>
            <button
              className={`btn ${picked === q.correct ? "" : "loje"}`}
              onClick={next}
            >
              {round + 1 >= TONIC_ROUNDS ? "Finish" : "Next"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ============================================================
   6 · Name the cadence — hear a two-chord move, name its function
   ============================================================ */
const CADENCE_ROUNDS = GAME_ROUNDS.cadence;

export function CadenceRound({ data, onExit, onAward }) {
  const keys = data.earKeys.length ? data.earKeys : earReadyKeys(data.unlocked);
  const [round, setRound] = useState(0);
  const [q, setQ] = useState(() => randomCadenceQ(pick(keys)));
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const awarded = useRef(false);

  const chords = useMemo(() => chordsForProgression(q.key, q.degrees), [q]);

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt === q.correct) setScore((s) => s + 1);
  };

  const next = () => {
    const nr = round + 1;
    if (nr >= CADENCE_ROUNDS) {
      if (!awarded.current) {
        awarded.current = true;
        onAward(gameXp("cadence", score));
      }
      setRound(nr);
      return;
    }
    setRound(nr);
    setQ(randomCadenceQ(pick(keys)));
    setPicked(null);
  };

  const restart = () => {
    awarded.current = false;
    setRound(0);
    setQ(randomCadenceQ(pick(keys)));
    setPicked(null);
    setScore(0);
  };

  if (round >= CADENCE_ROUNDS) {
    return (
      <GameDone
        title="good ear"
        sub="cadences named"
        cards={[
          { k: "correct", v: `${score}/${CADENCE_ROUNDS}`, accent: "var(--teal)" },
          { k: "XP earned", v: `+${gameXp("cadence", score)}`, accent: "var(--amber)" },
        ]}
        onAgain={restart}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <div className="game-head">
        <div className="qkind">key of {q.key}</div>
        <div className="game-score">
          {round + 1}/{CADENCE_ROUNDS}
        </div>
      </div>
      <div className="qarea">
        <div className="qkind">hear the move, name the cadence</div>
        <div className="ns-listen">
          <PlayButton chords={chords} big label="play the move" />
        </div>
        <div className="choices">
          {q.options.map((opt) => {
            let cls = "choice";
            if (picked) {
              if (opt === q.correct) cls += " right";
              else if (opt === picked) cls += " wrong";
              else cls += " dim";
            }
            return (
              <button key={opt} className={cls} onClick={() => choose(opt)}>
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className={`foot ${picked ? (picked === q.correct ? "right" : "wrong") : ""}`}>
        {picked && (
          <>
            <div className="verdict">
              <b>{picked === q.correct ? "yes!" : "listen again"}</b>
              <small>
                {picked === q.correct ? "that's the move" : `it was ${q.correct}`}
              </small>
            </div>
            <button
              className={`btn ${picked === q.correct ? "" : "loje"}`}
              onClick={next}
            >
              {round + 1 >= CADENCE_ROUNDS ? "Finish" : "Next"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

/* ---------------- shared end-of-game card ---------------- */
function GameDone({ title, sub, cards, onAgain, onExit }) {
  return (
    <div className="done-screen">
      <div className="trophy">
        <Icon name="trophy" />
      </div>
      <div className="big">{title}</div>
      <div className="sub">{sub}</div>
      <div className="scorecards">
        {cards.map((c) => (
          <div className="scorecard" key={c.k}>
            <div className="k">{c.k}</div>
            <div className="v" style={{ color: c.accent }}>
              {c.v}
            </div>
          </div>
        ))}
      </div>
      <button className="btn" style={{ maxWidth: 320, marginBottom: 12 }} onClick={onAgain}>
        Play again
      </button>
      <button className="btn ghost" style={{ maxWidth: 320 }} onClick={onExit}>
        Back to games
      </button>
    </div>
  );
}
